import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import {
  Settings, Users, Eye, AlertTriangle, BarChart3,
  Home, PlusCircle, Bell, Search, User, LogOut, Menu, X,
  Store, Zap, FileText, HelpCircle
} from 'lucide-react';
import { radarLogoSquare } from '@/lib/brand-assets';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const navigationItems = [
  {
    title: 'Overview',
    icon: Home,
    path: '/dashboard',
    badge: null
  },
  {
    title: 'Competitors',
    icon: Users,
    path: '/dashboard/competitors',
    badge: null
  },
  {
    title: 'Alerts',
    icon: AlertTriangle,
    path: '/dashboard/alerts',
    badge: null
  },
  {
    title: 'Store Connect',
    icon: Store,
    path: '/dashboard/store-connect',
    badge: null
  }
];

const quickActions = [
  {
    title: 'Add Competitor',
    icon: PlusCircle,
    path: '/dashboard/competitors/add',
    color: 'text-cta'
  },
  {
    title: 'Generate Report',
    icon: FileText,
    path: '/dashboard/analytics/report',
    color: 'text-secondary'
  }
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActivePath = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background font-body text-foreground">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 z-50 h-full w-64 bg-card border-r border-border/50 backdrop-blur-sm
        transform transition-transform duration-300 ease-in-out lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Sidebar header */}
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-border/50">
          <div className="flex items-center gap-3">
            <img
              src={radarLogoSquare}
              alt=""
              className="h-8 w-8 rounded-lg object-contain"
              width={32}
              height={32}
            />
            <div>
              <h1 className="font-heading text-lg font-bold text-foreground tracking-[0.18em]">RADAR</h1>
              <p className="text-xs text-muted-foreground">Intelligence Platform</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Navigation */}
        <div className="flex flex-col h-[calc(100vh-80px)]">
          <nav className="flex-1 p-4 space-y-1">
            {navigationItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg
                  transition-all duration-200 cursor-pointer
                  ${isActivePath(item.path)
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/10'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-4 h-4 shrink-0" />
                  <span className="font-medium">{item.title}</span>
                </div>
                {item.badge && (
                  <Badge
                    variant={item.badge === 'NEW' ? 'secondary' : 'destructive'}
                    className="text-[10px] px-1.5 py-0.5"
                  >
                    {item.badge}
                  </Badge>
                )}
              </button>
            ))}

            {/* Quick Actions Section */}
            <div className="pt-6 mt-6 border-t border-border/50">
              <div className="px-3 mb-3">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Quick Actions
                </h3>
              </div>
              <div className="space-y-1">
                {quickActions.map((action) => (
                  <button
                    key={action.path}
                    onClick={() => {
                      navigate(action.path);
                      setSidebarOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/10 transition-all duration-200 cursor-pointer"
                  >
                    <action.icon className={`w-4 h-4 shrink-0 ${action.color}`} />
                    <span>{action.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </nav>

          {/* User menu */}
          <div className="p-4 border-t border-border/50">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start p-2 h-auto">
                  <div className="flex items-center gap-3 text-left">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {user?.email?.split('@')[0] || 'User'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Free Plan
                      </div>
                    </div>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/help')}>
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Help & Support
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="lg:ml-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 h-16 bg-card/80 backdrop-blur-sm border-b border-border/50 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-4 h-4" />
            </Button>

            {/* Breadcrumb will go here */}
            <div className="hidden sm:block">
              <h2 className="font-heading text-lg font-semibold text-foreground">
                {navigationItems.find(item => isActivePath(item.path))?.title || 'Overview'}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <Button variant="ghost" size="sm" className="hidden md:flex">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>

            {/* Notifications (no unread badge wired yet) */}
            <Button variant="ghost" size="sm" className="relative">
              <Bell className="w-4 h-4" />
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="min-h-[calc(100vh-64px)]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}